import React from 'react';
import { Alert, Button, Stack, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { GmColumn, CpqColumn, LiveSourcesColumn } from './DrawerEvidenceColumns.jsx';
import { InvestigationFeed } from './ReviewPanels.jsx';
import { DrawerContextAlert, DrawerHeader, DrawerNavigationControls, evidenceStatusConfig, FileHealthSummary, PairwiseDeltas } from './VarianceDrawerSections.jsx';
import VarianceActionDialog from './VarianceActionDialog.jsx';
import DpeActionDialog from './DpeActionDialog.jsx';
import VarianceActionMenu from './VarianceActionMenu.jsx';
import VarianceAuditPanel from './VarianceAuditPanel.jsx';
import ProductDetailDialog from './ProductDetailDialog.jsx';
import VarianceHistoryDialog from './VarianceHistoryDialog.jsx';
import VarianceHistoryTimeline from './VarianceHistoryTimeline.jsx';
import BacDiffNarrativePanel from './BacDiffNarrativePanel.jsx';
import RecurringSuggestionsPanel from './RecurringSuggestionsPanel.jsx';
import LifecycleAlertsDrawerSummary from './LifecycleAlertsDrawerSummary.jsx';
import ReviewNoteDialog from './ReviewNoteDialog.jsx';
import ConfirmDialog from '../ConfirmDialog.jsx';

const VarianceDrawerBody = ({
  accountResolverContext,
  billingProductFamilyOrder,
  billingProductFamilyMinRows,
  bodyReview,
  canMarkComplete,
  catalogItems,
  confirmAction,
  context,
  cpqItems,
  currentUserName,
  displayTotals,
  drawerMode,
  draftVarianceAction,
  draftDpeAction,
  dpeActionError,
  dpeActions,
  evidencePending,
  evidencePendingReason,
  evidenceRows,
  fileHealthQuery,
  focusProductCode,
  gmItems,
  handleApplyNoteTemplate,
  handleApplyProductLink,
  handleConfirmAction,
  handleDeleteNote,
  handleEditNote,
  handleEvidenceDrop,
  handleEvidenceRemoveDrop,
  handleDpeActionDrop,
  handleDpeActionRemoveDrop,
  handleMarkComplete,
  handleResetReview,
  handleMarkReviewed,
  handleOpenAccountResolver,
  handleOpenVarianceMenu,
  handlePromoteRecurring,
  handleRefreshBacContext,
  handleSaveVarianceAction,
  handleSaveDpeAction,
  handleSelectVarianceAction,
  hasUnsavedVarianceActions,
  hasVarianceActions,
  historyEntry,
  isMatch,
  isProductCardDense,
  isRefreshingBacContext,
  lifecycleAlerts,
  isReviewed,
  isVarianceActionBusy,
  isVarianceActionSaveBusy,
  isDpeActionBusy,
  isResetReviewBusy = false,
  mockMode,
  mockSources,
  navigation,
  noteDraft,
  activeDraftNoteId,
  productDetailContext,
  program = 'website',
  recurringPromotionActionId,
  recurringSuggestions,
  recurringSuggestionsQuery,
  removeVarianceAction,
  removeDpeAction,
  requestResetAllVarianceActions,
  row,
  rowEvidenceQuery,
  runId,
  safeClose,
  saveAllVarianceActions,
  scheduleNoteSave,
  sealCurrentDraft,
  setCategory,
  setConfirmAction,
  setDraftVarianceAction,
  setDraftDpeAction,
  setDrawerMode,
  setHistoryEntry,
  setNoteDraft,
  setProductDetailContext,
  setRecurringPromotionActionId,
  setRemediation,
  setVarianceActionError,
  setVarianceMenuState,
  showReviewPanels,
  submitRecurringPromotion,
  varianceActionError,
  varianceActions,
  varianceMenuState,
  allowDpeActions = true,
}) => (
  <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
    <DrawerHeader
      row={row}
      review={bodyReview}
      isReviewed={isReviewed}
      drawerMode={drawerMode}
      onDrawerModeChange={setDrawerMode}
      evidenceStatus={evidenceStatusConfig({ mockMode, query: rowEvidenceQuery, row, runId })}
      peopleSoftId={evidenceRows.peopleSoftId}
      onClose={safeClose}
    />
    <DrawerNavigationControls navigation={navigation} />
    <RecurringSuggestionsPanel runId={runId} varianceId={row.id} bac={row.bac} query={recurringSuggestionsQuery} />
    <FileHealthSummary query={fileHealthQuery} varianceActions={varianceActions} context={context} />
    <LifecycleAlertsDrawerSummary runId={runId} bac={row.bac} context={context} prefetchedAlerts={lifecycleAlerts} />
    <DrawerContextAlert context={context} onResolveAccount={accountResolverContext ? handleOpenAccountResolver : null} />
    <BacDiffNarrativePanel
      evidenceRows={evidenceRows}
      sourceTotals={displayTotals}
      varianceActions={varianceActions}
      evidencePending={evidencePending}
      evidencePendingReason={evidencePendingReason}
    />
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
      <Button variant="outlined" color="inherit" startIcon={<RestartAltIcon />} onClick={requestResetAllVarianceActions} disabled={!hasVarianceActions || isVarianceActionBusy}>
        Reset changes
      </Button>
      <Button variant="outlined" color="warning" startIcon={<RestartAltIcon />} onClick={handleResetReview} disabled={isResetReviewBusy || !row?.review?.reviewed}>
        Reset review
      </Button>
      <Button variant="contained" color="primary" startIcon={<SaveOutlinedIcon />} onClick={saveAllVarianceActions} disabled={!hasUnsavedVarianceActions || isVarianceActionSaveBusy}>
        Save changes
      </Button>
      <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={handleMarkComplete} disabled={!canMarkComplete}>
        Mark BAC complete
      </Button>
    </Stack>
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems="stretch">
      <GmColumn total={displayTotals.gmTotal} originalTotal={displayTotals.originalTotals?.gmTotal ?? null} totalStatus={displayTotals.totalStatuses?.gm || null} items={gmItems} serviceMonth={row.serviceMonth} program={program} trailingWindow={row.trailingWindow || []} appsheetData={mockMode ? mockSources.appsheet : evidenceRows.appsheetData} onVarianceMenu={handleOpenVarianceMenu} onEvidenceDrop={handleEvidenceDrop} onEvidenceRemoveDrop={handleEvidenceRemoveDrop} draftActions={varianceActions} dense={isProductCardDense} productFamilyOrder={billingProductFamilyOrder} productFamilyMinRows={billingProductFamilyMinRows} focusProductCode={focusProductCode} recurringSuggestions={recurringSuggestions} />
      {(evidenceRows.historicalFinalGmLineItems || []).length ? (
        <GmColumn label="Final GM File" source="historical_final_gm" sourceLabel="Historical final GM" accent="success.main" total={displayTotals.cpqTotal} items={evidenceRows.historicalFinalGmLineItems || []} serviceMonth={row.serviceMonth} program={program} onVarianceMenu={null} onEvidenceDrop={null} draftActions={[]} dense={isProductCardDense} productFamilyOrder={billingProductFamilyOrder} productFamilyMinRows={billingProductFamilyMinRows} focusProductCode={focusProductCode} />
      ) : (
        <>
          <CpqColumn total={displayTotals.cpqTotal} originalTotal={displayTotals.originalTotals?.cpqTotal ?? null} totalStatus={displayTotals.totalStatuses?.cpq || null} items={cpqItems} serviceMonth={row.serviceMonth} program={program} appsheetData={mockMode ? mockSources.appsheet : evidenceRows.appsheetData} onVarianceMenu={handleOpenVarianceMenu} onEvidenceDrop={handleEvidenceDrop} onEvidenceRemoveDrop={handleEvidenceRemoveDrop} draftActions={varianceActions} dense={isProductCardDense} productFamilyOrder={billingProductFamilyOrder} productFamilyMinRows={billingProductFamilyMinRows} focusProductCode={focusProductCode} recurringSuggestions={recurringSuggestions} />
          <LiveSourcesColumn
            dpeItems={evidenceRows.dpeLineItems || []}
            dpeTotal={displayTotals.dpeTotal}
            dpeData={evidenceRows.dpeData}
            appsheetData={mockMode ? mockSources.appsheet : evidenceRows.appsheetData}
            dpeActions={dpeActions}
            tradeData={mockSources.trade}
            mockMode={mockMode}
            onVarianceMenu={handleOpenVarianceMenu}
            onEvidenceDrop={handleEvidenceDrop}
            onDpeActionDrop={handleDpeActionDrop}
            onDpeActionRemoveDrop={handleDpeActionRemoveDrop}
            onRefreshEvidence={mockMode ? null : handleRefreshBacContext}
            isRefreshingEvidence={isRefreshingBacContext}
            isLoadingEvidence={!mockMode && rowEvidenceQuery.isLoading}
            draftActions={varianceActions}
            dense={isProductCardDense}
            productFamilyOrder={billingProductFamilyOrder}
            productFamilyMinRows={billingProductFamilyMinRows}
            focusProductCode={focusProductCode}
            allowDpeActions={allowDpeActions}
            serviceMonth={row.serviceMonth}
            program={program}
          />
        </>
      )}
    </Stack>
    <PairwiseDeltas deltas={displayTotals.deltas} />
    {varianceActionError && (
      <Alert severity="error" onClose={() => setVarianceActionError('')}>
        {varianceActionError}
      </Alert>
    )}
    {dpeActionError && (
      <Alert severity="error">
        {dpeActionError?.message || String(dpeActionError)}
      </Alert>
    )}
    <VarianceAuditPanel actions={varianceActions} dpeActions={dpeActions} onRemove={removeVarianceAction} onRemoveDpe={removeDpeAction} onPromoteRecurring={handlePromoteRecurring} focusActionId={context?.focusActionId || context?.actionId || ''} />
    {mockMode ? (
      <VarianceHistoryTimeline history={mockSources.history} onSelectEntry={setHistoryEntry} />
    ) : (
      <Alert severity="info">Prior variance timeline is not connected to retained review history yet.</Alert>
    )}
    <VarianceHistoryDialog
      open={Boolean(historyEntry)}
      entry={historyEntry}
      dealerName={row.dealerName}
      bac={row.bac}
      onClose={() => setHistoryEntry(null)}
      onApplyTemplate={(entry) => {
        if (entry.category) setCategory(entry.category);
        if (entry.remediation) setRemediation(entry.remediation);
      }}
    />
    {showReviewPanels && (
      <>
        <InvestigationFeed
          currentUserName={currentUserName}
          noteDraft={noteDraft}
          activeDraftNoteId={activeDraftNoteId}
          setNoteDraft={setNoteDraft}
          scheduleNoteSave={scheduleNoteSave}
          sealCurrentDraft={sealCurrentDraft}
          onEditNote={handleEditNote}
          onDeleteNote={handleDeleteNote}
          onApplyTemplate={handleApplyNoteTemplate}
          notes={bodyReview.notes || []}
          row={row}
          gmItems={gmItems}
          cpqItems={cpqItems}
          appsheetData={mockMode ? mockSources.appsheet : evidenceRows.appsheetData}
          varianceActions={varianceActions}
          runId={runId}
        />
      </>
    )}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
      <Tooltip title="Opens the dealer detail page in a new tab.">
        <Button variant="outlined" startIcon={<OpenInNewIcon />} href={`/dealer/${row.bac}`} target="_blank" rel="noopener">
          Dealer detail
        </Button>
      </Tooltip>
      {!isMatch && isReviewed && (
        <Button variant="outlined" color="success" startIcon={<CheckCircleIcon />} onClick={handleMarkReviewed}>
          Reviewed - undo
        </Button>
      )}
    </Stack>
    <VarianceActionMenu state={varianceMenuState} onClose={() => setVarianceMenuState(null)} onSelect={handleSelectVarianceAction} allowDpeActions={allowDpeActions} />
    <VarianceActionDialog draft={draftVarianceAction} currentUserName={currentUserName} onClose={() => setDraftVarianceAction(null)} onSave={handleSaveVarianceAction} gmItems={gmItems} cpqItems={cpqItems} dpeItems={evidenceRows.dpeLineItems || []} appsheetData={mockMode ? mockSources.appsheet : evidenceRows.appsheetData} appSheetAccounts={mockMode ? null : evidenceRows.appSheetAccounts} catalogItems={catalogItems} />
    <DpeActionDialog draft={allowDpeActions ? draftDpeAction : null} onClose={() => setDraftDpeAction(null)} onSave={handleSaveDpeAction} busy={isDpeActionBusy} error={dpeActionError} catalogItems={catalogItems} />
    <ProductDetailDialog context={productDetailContext} currentUserName={currentUserName} onClose={() => setProductDetailContext(null)} onLink={handleApplyProductLink} gmItems={gmItems} cpqItems={cpqItems} dpeItems={evidenceRows.dpeLineItems || []} appsheetData={mockMode ? mockSources.appsheet : evidenceRows.appsheetData} appSheetAccounts={mockMode ? null : evidenceRows.appSheetAccounts} runId={runId} />
    <ReviewNoteDialog open={Boolean(recurringPromotionActionId)} title="Promote Recurring Match Rule" label="Optional audit note" confirmLabel="Promote Rule" onClose={() => setRecurringPromotionActionId(null)} onConfirm={submitRecurringPromotion} />
    <ConfirmDialog
      open={Boolean(confirmAction)}
      title={confirmAction === 'reset' ? 'Reset variance changes?' : 'Close with unsaved changes?'}
      description={confirmAction === 'reset'
        ? 'This clears every draft and saved variance action for this BAC. This cannot be undone.'
        : 'This BAC has draft variance actions. Close the drawer and discard the in-progress review context?'}
      confirmLabel={confirmAction === 'reset' ? 'Reset changes' : 'Close drawer'}
      confirmColor={confirmAction === 'reset' ? 'error' : 'warning'}
      onConfirm={handleConfirmAction}
      onCancel={() => setConfirmAction(null)}
    />
  </Stack>
);

export default VarianceDrawerBody;
